import { useEffect, useState } from 'react'
import { supabase } from '../../services/supabase'
import { useSimpleHqAccess } from './useSimpleHqAccess'

type LeaveType = {
  id: string
  code: string
  name: string
  default_days: number
  requires_approval: boolean
  allow_half_day: boolean
  allow_negative: boolean
  active: boolean
  sort_order: number
}

type Blackout = { id: string; name: string; start_date: string; end_date: string }

export function LeaveTypesView() {
  const { companyId } = useSimpleHqAccess()
  const [types, setTypes] = useState<LeaveType[]>([])
  const [blackouts, setBlackouts] = useState<Blackout[]>([])
  const [form, setForm] = useState({
    code: '',
    name: '',
    default_days: '0',
    allow_half_day: true,
    allow_negative: false,
  })
  const [bo, setBo] = useState({ name: '', start_date: '', end_date: '' })

  const load = async () => {
    const [{ data: t }, { data: b }] = await Promise.all([
      supabase.from('leave_types').select('*').eq('company_id', companyId).order('sort_order'),
      supabase.from('leave_blackouts').select('id, name, start_date, end_date').eq('company_id', companyId).order('start_date'),
    ])
    setTypes((t as LeaveType[]) || [])
    setBlackouts((b as Blackout[]) || [])
  }

  useEffect(() => {
    void load()
  }, [companyId])

  const saveType = async (row: LeaveType) => {
    const { error } = await supabase
      .from('leave_types')
      .update({
        name: row.name,
        default_days: row.default_days,
        requires_approval: row.requires_approval,
        allow_half_day: row.allow_half_day,
        allow_negative: row.allow_negative,
        active: row.active,
        updated_at: new Date().toISOString(),
      })
      .eq('id', row.id)
    if (error) alert(error.message)
    else await load()
  }

  const createType = async (e: React.FormEvent) => {
    e.preventDefault()
    const { error } = await supabase.from('leave_types').insert({
      company_id: companyId,
      code: form.code.trim().toLowerCase().replace(/\s+/g, '_'),
      name: form.name.trim(),
      default_days: Number(form.default_days),
      allow_half_day: form.allow_half_day,
      allow_negative: form.allow_negative,
      requires_approval: true,
      sort_order: types.length + 1,
    })
    if (error) alert(error.message)
    else {
      setForm({ code: '', name: '', default_days: '0', allow_half_day: true, allow_negative: false })
      await load()
    }
  }

  const addBlackout = async (e: React.FormEvent) => {
    e.preventDefault()
    const { error } = await supabase.from('leave_blackouts').insert({
      company_id: companyId,
      name: bo.name,
      start_date: bo.start_date,
      end_date: bo.end_date,
    })
    if (error) alert(error.message)
    else {
      setBo({ name: '', start_date: '', end_date: '' })
      await load()
    }
  }

  return (
    <div>
      <p className="hq-eyebrow">Admin</p>
      <h1 className="hq-title">Leave types</h1>
      <p className="hq-sub">Defaults, half-day, negative balance, and blackout windows.</p>

      <div className="hq-panel mb-4">
        <div className="hq-table-wrap">
          <table className="hq-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Default days</th>
                <th>Half</th>
                <th>Neg</th>
                <th>Active</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {types.map((t) => (
                <tr key={t.id}>
                  <td>{t.code}</td>
                  <td>
                    <input
                      className="border rounded px-1"
                      value={t.name}
                      onChange={(e) =>
                        setTypes((prev) => prev.map((x) => (x.id === t.id ? { ...x, name: e.target.value } : x)))
                      }
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      className="w-20 border rounded px-1"
                      value={t.default_days}
                      onChange={(e) =>
                        setTypes((prev) =>
                          prev.map((x) => (x.id === t.id ? { ...x, default_days: Number(e.target.value) } : x)),
                        )
                      }
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={t.allow_half_day}
                      onChange={(e) =>
                        setTypes((prev) =>
                          prev.map((x) => (x.id === t.id ? { ...x, allow_half_day: e.target.checked } : x)),
                        )
                      }
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={t.allow_negative}
                      onChange={(e) =>
                        setTypes((prev) =>
                          prev.map((x) => (x.id === t.id ? { ...x, allow_negative: e.target.checked } : x)),
                        )
                      }
                    />
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={t.active}
                      onChange={(e) =>
                        setTypes((prev) => prev.map((x) => (x.id === t.id ? { ...x, active: e.target.checked } : x)))
                      }
                    />
                  </td>
                  <td>
                    <button type="button" className="hq-btn" onClick={() => void saveType(t)}>
                      Save
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <form className="hq-panel max-w-xl mb-4" onSubmit={createType}>
        <p className="hq-eyebrow">New type</p>
        <div className="hq-row">
          <div className="hq-field">
            <label>Code</label>
            <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} required />
          </div>
          <div className="hq-field">
            <label>Name</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          </div>
          <div className="hq-field">
            <label>Default days</label>
            <input value={form.default_days} onChange={(e) => setForm({ ...form, default_days: e.target.value })} />
          </div>
        </div>
        <button type="submit" className="hq-btn hq-btn-primary">
          Add type
        </button>
      </form>

      <div className="hq-panel">
        <p className="hq-eyebrow">Blackouts</p>
        <ul className="hq-list mb-3">
          {blackouts.map((b) => (
            <li key={b.id}>
              {b.name}: {b.start_date} → {b.end_date}
            </li>
          ))}
          {blackouts.length === 0 && <li className="hq-empty">No blackouts</li>}
        </ul>
        <form onSubmit={addBlackout}>
          <div className="hq-row">
            <div className="hq-field">
              <label>Name</label>
              <input value={bo.name} onChange={(e) => setBo({ ...bo, name: e.target.value })} required />
            </div>
            <div className="hq-field">
              <label>Start</label>
              <input type="date" value={bo.start_date} onChange={(e) => setBo({ ...bo, start_date: e.target.value })} required />
            </div>
            <div className="hq-field">
              <label>End</label>
              <input type="date" value={bo.end_date} onChange={(e) => setBo({ ...bo, end_date: e.target.value })} required />
            </div>
          </div>
          <button type="submit" className="hq-btn">
            Add blackout
          </button>
        </form>
      </div>
    </div>
  )
}
